import type { Theme } from '@mui/material';
import { LABEL_ATTR, PHONE_QUERY, STACK_ATTR } from '@/utils/stackedTables';

const TABLE = `table[${STACK_ATTR}]`;
const ROWS = [`${TABLE} > tbody > tr`, `${TABLE} > tfoot > tr`];
const CELLS = ROWS.flatMap((row) => [`${row} > td`, `${row} > th`]);
const LABELLED = `[${LABEL_ATTR}]:not([${LABEL_ATTR}=""])`;
const INLINE = ':is(button, a, span)';
const each = (selectors: string[], suffix = '') =>
  selectors.map((selector) => `${selector}${suffix}`).join(', ');

/**
 * The card look of a stacked table (see `utils/stackedTables`): one bordered
 * card per row, each cell a line with its column's name on the left and the
 * value on the right. `!important` only where pages set sizes inline (column
 * widths, a table `minWidth`) that would keep the table wide.
 */
export const stackedTableStyles = (theme: Theme) => ({
  [`@media ${PHONE_QUERY}`]: {
    [TABLE]: {
      display: 'block',
      width: '100% !important',
      minWidth: '0 !important',
      padding: theme.spacing(1)
    },
    // Cards scroll with the page: a box of fixed height (stock count, bulk
    // price preview) would show one card and a half and trap the finger.
    [`.MuiTableContainer-root:has(> ${TABLE})`]: {
      maxHeight: 'none'
    },
    [`${TABLE} > thead`]: {
      display: 'none'
    },
    [each([`${TABLE} > tbody`, `${TABLE} > tfoot`])]: {
      display: 'block'
    },
    [each(ROWS)]: {
      display: 'block',
      height: 'auto',
      padding: theme.spacing(0.5, 1.5),
      marginBottom: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius
    },
    // A row of one spanning cell (no data, loading, a row's expandable
    // details) is not a card of its own.
    [each(ROWS, ':has(> td[colspan]:only-child)')]: {
      padding: 0,
      border: 0,
      marginBottom: 0
    },
    [each(CELLS)]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      width: 'auto !important',
      minWidth: '0 !important',
      maxWidth: 'none !important',
      padding: `${theme.spacing(0.75, 0)} !important`,
      border: 0,
      textAlign: 'right',
      position: 'static',
      overflowWrap: 'anywhere'
    },
    // Values may shrink and wrap (some cells are `noWrap` for desktop).
    [each(CELLS, ' > *')]: {
      minWidth: 0,
      maxWidth: '100%'
    },
    [each(CELLS, ' .MuiTypography-noWrap')]: {
      whiteSpace: 'normal'
    },
    // Line spacing of the table's text would push values off their label.
    [each(CELLS, ' .MuiTypography-gutterBottom')]: {
      marginBottom: 0
    },
    // A labelled cell is a two-column grid: the name on the left, beside
    // the value's first part; further parts (a name and its company, a date
    // and its time) stack under it on the right.
    [each(CELLS, LABELLED)]: {
      display: 'grid',
      gridTemplateColumns: 'fit-content(45%) minmax(0, 1fr)',
      columnGap: theme.spacing(2),
      rowGap: 0,
      alignItems: 'center',
      justifyItems: 'end'
    },
    // Desktop spacing (centring, gaps under a line) would pull values off
    // the right edge or away from their name.
    [each(CELLS, `${LABELLED} > *`)]: {
      gridColumn: 2,
      margin: 0
    },
    [each(CELLS, `${LABELLED}::before`)]: {
      content: `attr(${LABEL_ATTR})`,
      gridColumn: 1,
      gridRow: 1,
      justifySelf: 'start',
      textAlign: 'left',
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.secondary
    },
    // Several buttons or links side by side (row actions) stay in a row.
    [each(CELLS, `${LABELLED}:has(> ${INLINE} + ${INLINE})`)]: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.5)
    },
    [each(CELLS, `${LABELLED}:has(> ${INLINE} + ${INLINE})::before`)]: {
      marginRight: 'auto'
    },
    // No column name (a checkbox column): the value starts on the left.
    [each(CELLS, `[${LABEL_ATTR}=""]`)]: {
      justifyContent: 'flex-start'
    },
    [each(CELLS, '[colspan]')]: {
      display: 'block',
      textAlign: 'center'
    }
  }
});
